import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import { AttachmentLink } from '../types'
import { CellRendererProps, ATTACHMENT_KEYS } from './types'
import FileCollaborationTool from '../../file_collaboration_tool_app/file_collaboration_tool'
import PhotoswipeLink from '../../../shared/photoswipe/photoswipe_link'
import PhotoswipeGalleryLink from '../../../shared/photoswipe/photoswipe_gallery_link'

const isPdf = (name: string): boolean => name.toLowerCase().endsWith('.pdf')

const imageAttachmentsForGallery = (attachments: AttachmentLink[], galleryId: string): AttachmentLink[] =>
  attachments
    .filter((attachment) => attachment.isImage && attachment.thumbUrl && attachment.src)
    .map((attachment) => ({
      ...attachment,
      filename: attachment.name,
      galleryId,
    }))

export function CommentAttachmentsCell({ value, column, rowId }: CellRendererProps): React.ReactNode {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }

  const images = imageAttachmentsForGallery(value as AttachmentLink[], `comment-attachments-${rowId}`)

  if (images.length === 0) {
    return null
  }

  return (
    <PhotoswipeGalleryLink
      images={images}
      galleryId={`comment-attachments-${rowId}`}
      img_alt={column.label}
    />
  )
}

export function AttachmentCell({ value, column, rowId, adobePreviewFileOptions }: CellRendererProps): React.ReactNode {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // Don't close if clicking inside a modal (FileCollaborationTool uses a portal)
      if ((target as Element).closest?.('.modal')) {
        return
      }
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  if (!Array.isArray(value) || value.length === 0) {
    return null
  }

  const attachments = value as AttachmentLink[]
  const isDesignFiles = column.key === 'design_files'

  const renderAttachmentLink = (attachment: AttachmentLink) => {
    if (isDesignFiles && isPdf(attachment.name) && adobePreviewFileOptions && attachment.id && attachment.presignedUrl) {
      return (
        <FileCollaborationTool
          adobePreviewFileOptions={adobePreviewFileOptions}
          file={{ presignedUrl: attachment.presignedUrl, name: attachment.name, id: attachment.id }}
          thumb={false}
        />
      )
    }

    const href = attachment.downloadUrl || attachment.url
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {attachment.name}
      </a>
    )
  }

  return (
    <div className="attachment-cell position-relative" ref={containerRef}>
      <button
        type="button"
        className="btn btn-link p-0"
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="far fa-file" />
        <span className="ml-1">{attachments.length}</span>
      </button>

      {isOpen && (
        <div className="attachment-popup">
          <ul className="list-unstyled mb-0">
            {attachments.map((attachment, idx) => (
              <li key={idx} className="d-flex align-items-center py-1">
                {attachment.isImage && attachment.thumbUrl && attachment.src ? (
                  <PhotoswipeLink
                    src={attachment.src}
                    galleryId={`attachment-popup-${column.key}-${rowId}`}
                    title={attachment.name}
                    filename={attachment.name}
                    downloadUrl={attachment.downloadUrl}
                    w={attachment.w}
                    h={attachment.h}
                    className="d-flex align-items-center"
                  >
                    <span className="attachment-preview mr-2">
                      <img src={attachment.thumbUrl} alt={attachment.name} />
                    </span>
                    {attachment.name}
                  </PhotoswipeLink>
                ) : (
                  <>
                    <span className="attachment-preview mr-2">
                      <i className="far fa-file" />
                    </span>
                    {renderAttachmentLink(attachment)}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export function isAttachmentColumn(columnKey: string): boolean {
  return ATTACHMENT_KEYS.includes(columnKey)
}

export function isCommentAttachmentColumn(columnKey: string): boolean {
  return columnKey === 'comment_attachments'
}
